/**
 * Reviewer (design-reference §4.6) — keyboard-first single-item triage. A split
 * pane: LEFT the document preview and its real OCR-derived signals; RIGHT the AI
 * summary, the model's suggestion (name + confidence + rationale) and the decision
 * panel — assign an existing category (searchable Combobox), create a new one
 * (inline form → offers the sweep), or leave as Other. Every decision is a real
 * `POST /v1/review/{id}/resolve`; a 409 (already resolved elsewhere) surfaces as a
 * conflict state. Keyboard: j/↓ next · k/↑ prev · 1 accept · 2 create · 3 other ·
 * Esc back to the queue (disabled inside inputs, and while the sweep modal is up).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../api/errors';
import type { Category, ResolveRequest, ResolveResponse, SweepResponse } from '../../api/schema';
import { useSession } from '../../auth/session';
import {
  Button,
  Combobox,
  ConfidenceMeter,
  ConfirmModal,
  ErrorState,
  Input,
  Skeleton,
  useToast,
  type ComboboxOption,
} from '../../components';
import { hasStoredOriginal } from '../documents/preview';
import { PreviewBody, PreviewNotice } from '../documents/PreviewBody';
import { useDocumentPreview } from '../documents/useDocumentPreview';

import './review.css';
import { ageSpoken, reasonLabel, reasonTone, resolveSuggestionName } from './format';
import { REVIEW_CHANGED_EVENT } from './ReviewQueueScreen';
import { useReviewApi } from './reviewApi';
import { useReviewer, type ReviewerData } from './useReviewer';
import { SweepModal } from './SweepModal';

function announceReviewChanged(): void {
  window.dispatchEvent(new CustomEvent(REVIEW_CHANGED_EVENT));
}

interface SweepTarget {
  categoryId: string;
  categoryName: string;
  affected: number;
}

/** A resolution staged for confirmation — nothing commits until it's confirmed. */
type PendingDecision =
  | { kind: 'assign'; categoryId: string; categoryName: string }
  | { kind: 'create'; name: string; description: string }
  | { kind: 'leave' };

export function ReviewerScreen(): React.JSX.Element {
  const { reviewItemId = '' } = useParams<{ reviewItemId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = useSession();
  const api = useReviewApi();

  const state = useReviewer(reviewItemId);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [sweep, setSweep] = useState<SweepTarget | null>(null);
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const go = useCallback((to: string) => { void navigate(to); }, [navigate]);
  const backToQueue = useCallback(() => { go('/review'); }, [go]);

  // Reset the transient decision UI whenever the item changes.
  useEffect(() => {
    setCreateOpen(false);
    setName('');
    setDescription('');
    setConflict(false);
    setSweep(null);
  }, [reviewItemId]);

  const data = state.status === 'ready' ? state.data : null;

  /** After a resolution the item leaves the queue: go to the next, or home. */
  const advance = useCallback(() => {
    announceReviewChanged();
    if (data?.nextId != null && data.nextId !== reviewItemId) go(`/review/${data.nextId}`);
    else backToQueue();
  }, [data, reviewItemId, go, backToQueue]);

  const resolve = useCallback(
    async (body: ResolveRequest, onDone: (res: ResolveResponse) => void) => {
      if (data == null || busy) return;
      setBusy(true);
      try {
        const res = await api.resolve(reviewItemId, body);
        onDone(res);
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          setConflict(true);
        } else {
          toast('That decision could not be saved — please try again.', { tone: 'error' });
        }
      } finally {
        setBusy(false);
      }
    },
    [api, data, busy, reviewItemId, toast],
  );

  const assignExisting = useCallback(
    (categoryId: string, categoryName: string) => {
      void resolve({ action: 'assign_existing', category_id: categoryId }, () => {
        toast(`Assigned "${categoryName}" — correction logged`);
        advance();
      });
    },
    [resolve, toast, advance],
  );

  const leaveAsOther = useCallback(() => {
    void resolve({ action: 'dismiss' }, () => {
      toast('Left as Other');
      advance();
    });
  }, [resolve, toast, advance]);

  const submitCreate = useCallback(
    (categoryName: string, categoryDescription: string) => {
      const trimmed = categoryName.trim();
      if (trimmed === '') return;
      void resolve(
        { action: 'create_category', name: trimmed, description: categoryDescription.trim() || null },
        (res: ResolveResponse) => {
          const created: Category | null = res.created_category;
          toast(`"${trimmed}" added to the taxonomy`);
          if (created != null && res.sweep != null) {
            // Offer the sweep before advancing.
            setSweep({
              categoryId: created.id,
              categoryName: created.name,
              affected: res.sweep.affected,
            });
            announceReviewChanged();
          } else {
            advance();
          }
        },
      );
    },
    [resolve, toast, advance],
  );

  const acceptSuggestion = useCallback(() => {
    if (data == null) return;
    // `proposedNew` is only ever true when `data.item.suggested_class` itself
    // is a genuinely new name (see useReviewer's derivation), so this never
    // sends an existing category into the "create new" branch below.
    const suggested = resolveSuggestionName(data.item, data.document);
    if (suggested == null || suggested.trim() === '') return;
    if (data.proposedNew) {
      setCreateOpen(true);
      setName((n) => (n === '' ? suggested : n));
      window.setTimeout(() => nameRef.current?.focus(), 0);
      return;
    }
    const match = data.categories.find(
      (c) => c.name.trim().toLowerCase() === suggested.trim().toLowerCase(),
    );
    if (match != null) setPending({ kind: 'assign', categoryId: match.id, categoryName: match.name });
  }, [data]);

  const confirmPending = useCallback(() => {
    if (pending == null) return;
    setPending(null);
    if (pending.kind === 'assign') assignExisting(pending.categoryId, pending.categoryName);
    else if (pending.kind === 'create') submitCreate(pending.name, pending.description);
    else leaveAsOther();
  }, [pending, assignExisting, submitCreate, leaveAsOther]);

  const cancelPending = useCallback(() => {
    setPending(null);
  }, []);

  // --- keyboard (design-reference §8.2) --------------------------------------
  useEffect(() => {
    if (data == null) return;
    const onKey = (event: KeyboardEvent): void => {
      // While the sweep modal or the confirm dialog is open, only its own Esc
      // (handled by the Modal) applies — the triage shortcuts are inert.
      if (sweep != null || pending != null) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        if (event.key === 'Escape') target?.blur();
        return;
      }
      switch (event.key) {
        case 'j':
        case 'ArrowDown':
          if (data.nextId != null) { event.preventDefault(); go(`/review/${data.nextId}`); }
          break;
        case 'k':
        case 'ArrowUp':
          if (data.prevId != null) { event.preventDefault(); go(`/review/${data.prevId}`); }
          break;
        case '1':
          // Accepting a proposed-new-category suggestion only opens the
          // (categories:manage-gated) create form — see the matching note on
          // the Accept button's own gating in ReviewerBody.
          if (hasPermission(data.proposedNew ? 'categories:manage' : 'review:resolve')) {
            event.preventDefault();
            acceptSuggestion();
          }
          break;
        case '2':
          if (hasPermission('categories:manage')) {
            event.preventDefault();
            setCreateOpen((v) => !v);
          }
          break;
        case '3':
          if (hasPermission('review:resolve')) {
            event.preventDefault();
            setPending({ kind: 'leave' });
          }
          break;
        case 'Escape':
          event.preventDefault();
          backToQueue();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [data, sweep, pending, go, acceptSuggestion, backToQueue, hasPermission]);

  // --- loading ---------------------------------------------------------------
  if (state.status === 'loading') {
    return (
      <div className="ds-reviewer" aria-busy="true">
        <Skeleton width={280} height={16} />
        <div className="ds-reviewer__body">
          <div className="ds-card ds-reviewer__pane"><Skeleton height={320} radius={8} /></div>
          <div className="ds-card ds-reviewer__pane"><Skeleton height={320} radius={8} /></div>
        </div>
        <p className="ds-review__loading">Loading the document…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="ds-reviewer">
        <ErrorState
          title="We couldn't load this review item"
          body="The review service didn't respond. Try again, or head back to the queue."
          traceId={state.traceId ?? undefined}
          actions={
            <>
              <Button variant="primary" onClick={state.reload}>Retry</Button>
              <Button variant="secondary" onClick={backToQueue}>Back to review queue</Button>
            </>
          }
        />
      </div>
    );
  }

  if (state.status === 'gone') {
    return (
      <div className="ds-reviewer">
        <ConflictCard onBack={backToQueue} />
      </div>
    );
  }

  const prevId = state.data.prevId;
  const nextId = state.data.nextId;

  return (
    <>
      <ReviewerBody
        data={state.data}
        conflict={conflict}
        busy={busy}
        createOpen={createOpen}
        name={name}
        description={description}
        nameRef={nameRef}
        onBack={backToQueue}
        onPrev={prevId != null ? () => { go(`/review/${prevId}`); } : undefined}
        onNext={nextId != null ? () => { go(`/review/${nextId}`); } : undefined}
        onAccept={acceptSuggestion}
        onAssign={(categoryId, categoryName) => { setPending({ kind: 'assign', categoryId, categoryName }); }}
        onLeaveOther={() => { setPending({ kind: 'leave' }); }}
        onToggleCreate={() => { setCreateOpen((v) => !v); }}
        onName={setName}
        onDescription={setDescription}
        onSubmitCreate={() => { setPending({ kind: 'create', name, description }); }}
        onCancelCreate={() => { setCreateOpen(false); }}
        sweepNode={
          sweep != null ? (
            <SweepModal
              open
              api={api}
              categoryId={sweep.categoryId}
              categoryName={sweep.categoryName}
              initialAffected={sweep.affected}
              onSkip={() => { setSweep(null); advance(); }}
              onComplete={(res: SweepResponse) => {
                const n = res.enqueued ?? res.affected ?? 0;
                toast(`Sweep complete — ${n.toLocaleString('en-GB')} document${n === 1 ? '' : 's'} re-classified as "${res.category_name}"`);
                setSweep(null);
                advance();
              }}
              onError={(m) => { toast(m, { tone: 'error' }); }}
            />
          ) : null
        }
      />
      <ConfirmModal
        open={pending != null}
        onCancel={cancelPending}
        onConfirm={confirmPending}
        title={
          pending?.kind === 'assign'
            ? 'Assign to this category?'
            : pending?.kind === 'create'
              ? 'Create this category?'
              : 'Leave as Other?'
        }
      >
        {pending?.kind === 'assign' ? (
          <>
            Assign this document to "<strong>{pending.categoryName}</strong>"?
          </>
        ) : null}
        {pending?.kind === 'create' ? (
          <>
            Create "<strong>{pending.name}</strong>" and assign this document to it?
          </>
        ) : null}
        {pending?.kind === 'leave' ? 'This document will be marked Other.' : null}
      </ConfirmModal>
    </>
  );
}

// --- presentational body -----------------------------------------------------

interface BodyProps {
  data: ReviewerData;
  conflict: boolean;
  busy: boolean;
  createOpen: boolean;
  name: string;
  description: string;
  nameRef: React.RefObject<HTMLInputElement>;
  onBack: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onAccept: () => void;
  onAssign: (id: string, name: string) => void;
  onLeaveOther: () => void;
  onToggleCreate: () => void;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
  onSubmitCreate: () => void;
  onCancelCreate: () => void;
  sweepNode: React.ReactNode;
}

function ReviewerBody(props: BodyProps): React.JSX.Element {
  const { hasPermission } = useSession();
  const { data, conflict } = props;
  const { item, document, index, total } = data;
  const filename = item.document?.filename ?? item.document_id;
  const previewable = document != null && hasStoredOriginal(document.status);
  const previewState = useDocumentPreview(item.document_id, document?.mime ?? '', previewable);

  if (conflict) {
    return <ConflictCard onBack={props.onBack} />;
  }

  const canResolve = hasPermission('review:resolve');
  const canCreateCategory = hasPermission('categories:manage');
  // For a proposed-new-category suggestion, Accept only opens the (also
  // categories:manage-gated) create form — it creates nothing itself. Gating
  // it on review:resolve alone would render a button that does nothing
  // visible for someone who lacks categories:manage.
  const canAccept = data.proposedNew ? canCreateCategory : canResolve;

  const summary = document?.summary ?? item.document?.summary ?? null;
  const suggestionName = resolveSuggestionName(item, document);
  const rationale = document?.classification?.rationale ?? item.suggested_description ?? null;

  const categoryOptions: ComboboxOption[] = data.categories.map((c) => ({ value: c.id, label: c.name }));

  return (
    <div className="ds-reviewer">
      <div className="ds-reviewer__head">
        <button type="button" className="ds-linkbtn" onClick={props.onBack}>
          ← Review queue
        </button>
        <span className="ds-reviewer__slash" aria-hidden="true">/</span>
        <span className="ds-reviewer__file" title={filename}>{filename}</span>
        <span className={`ds-reason ds-reason--${reasonTone(item.reason)}`}>{reasonLabel(item.reason)}</span>
        <span className="ds-reviewer__age">{ageSpoken(item.created_at)} in queue</span>
        <span className="ds-reviewer__nav">
          <span className="ds-reviewer__index">{index} of {total} open</span>
          <button
            type="button"
            className="ds-iconbtn"
            title="Previous (k / ↑)"
            aria-label="Previous item"
            disabled={props.onPrev === undefined}
            onClick={props.onPrev}
          >↑</button>
          <button
            type="button"
            className="ds-iconbtn"
            title="Next (j / ↓)"
            aria-label="Next item"
            disabled={props.onNext === undefined}
            onClick={props.onNext}
          >↓</button>
        </span>
      </div>

      <div className="ds-reviewer__body">
        {/* LEFT: preview + real signals */}
        <div className="ds-reviewer__col">
          <div className="ds-card ds-preview">
            <div className="ds-preview__head">Preview · page 1</div>
            <div className="ds-preview__stage">
              {document != null ? (
                <PreviewBody state={previewState} doc={document} zoom={1} previewable={previewable} />
              ) : (
                <PreviewNotice title="No preview" body="This document's details are not available." />
              )}
            </div>
          </div>
          <div className="ds-card ds-signals">
            <p className="ds-eyebrow">Document signals</p>
            <dl className="ds-signals__grid">
              <div><dt>Status</dt><dd>{(document?.status ?? item.document?.status ?? '—')}</dd></div>
              <div><dt>Pages</dt><dd>{document?.page_count ?? '—'}</dd></div>
              <div><dt>Language</dt><dd>{document?.language ?? '—'}</dd></div>
              <div>
                <dt>OCR confidence</dt>
                <dd>{document?.ocr_confidence != null ? document.ocr_confidence.toFixed(2) : '—'}</dd>
              </div>
              <div><dt>Source</dt><dd>{document?.capture_metadata.source.channel ?? '—'}</dd></div>
              <div>
                <dt>Ingested</dt>
                <dd>{document?.ingested_at != null ? new Date(document.ingested_at).toLocaleDateString('en-GB') : '—'}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* RIGHT: summary + suggestion + decision */}
        <div className="ds-reviewer__col">
          <div className="ds-card ds-aisum">
            <p className="ds-eyebrow ds-aisum__eyebrow">AI summary</p>
            <p className="ds-aisum__body">
              {summary ?? 'No summary was produced for this document.'}
            </p>
          </div>

          <div className="ds-card ds-suggest">
            <p className="ds-eyebrow">Model suggestion</p>
            {suggestionName != null ? (
              <div className="ds-suggest__head">
                <span className="ds-suggest__name">{suggestionName}</span>
                {data.proposedNew ? <span className="ds-newchip">PROPOSED NEW CATEGORY</span> : null}
                <span className="ds-suggest__meter">
                  <ConfidenceMeter value={item.confidence} size={7} label="model confidence" />
                </span>
              </div>
            ) : (
              <p className="ds-suggest__none">No confident suggestion — this one needs your judgement.</p>
            )}
            {rationale != null ? (
              <p className="ds-suggest__why">
                <span className="ds-suggest__why-label">Why:</span> {rationale}
              </p>
            ) : null}
          </div>

          <div className="ds-card ds-decision">
            <div className="ds-decision__head">
              <h2 className="ds-decision__title">Decision</h2>
              <span className="ds-decision__audit">every action is audited</span>
            </div>

            {canAccept && suggestionName != null ? (
              <button
                type="button"
                className="ds-decision__accept"
                onClick={props.onAccept}
                disabled={props.busy}
              >
                <kbd className="ds-kbd-inline">1</kbd>
                <span>{data.proposedNew ? `Create "${suggestionName}"` : `Assign "${suggestionName}"`}</span>
                <span className="ds-decision__rec">recommended</span>
              </button>
            ) : null}

            {canResolve ? (
              <div className="ds-decision__block">
                <p className="ds-decision__label">Assign an existing category</p>
                <Combobox
                  label="Assign an existing category"
                  hideLabel
                  options={categoryOptions}
                  value={null}
                  placeholder="Search categories…"
                  emptyText="No matches — create it below"
                  onChange={(id) => {
                    const cat = data.categories.find((c) => c.id === id);
                    if (cat != null) props.onAssign(cat.id, cat.name);
                  }}
                />
              </div>
            ) : null}

            {canCreateCategory ? (
              <div className="ds-decision__block">
                <button
                  type="button"
                  className="ds-decision__createhead"
                  onClick={props.onToggleCreate}
                  aria-expanded={props.createOpen}
                >
                  <kbd className="ds-kbd-inline ds-kbd-inline--muted">2</kbd>
                  <span>Create a new category</span>
                  <span aria-hidden="true">{props.createOpen ? '▴' : '▾'}</span>
                </button>
                {props.createOpen ? (
                  <div className="ds-createform">
                    <Input
                      ref={props.nameRef}
                      aria-label="Category name"
                      placeholder="Category name"
                      value={props.name}
                      onChange={(e) => { props.onName(e.currentTarget.value); }}
                    />
                    <Input
                      aria-label="Short description"
                      placeholder="Short description (helps the classifier)"
                      value={props.description}
                      onChange={(e) => { props.onDescription(e.currentTarget.value); }}
                    />
                    <div className="ds-createform__actions">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={props.name.trim() === '' || props.busy}
                        onClick={props.onSubmitCreate}
                      >
                        Create &amp; offer sweep
                      </Button>
                      <Button variant="secondary" size="sm" onClick={props.onCancelCreate}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {canResolve ? (
              <button
                type="button"
                className="ds-decision__other"
                onClick={props.onLeaveOther}
                disabled={props.busy}
              >
                <kbd className="ds-kbd-inline ds-kbd-inline--muted">3</kbd>
                <span>Leave as Other</span>
                <span className="ds-decision__skip">skip for now</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {props.sweepNode}
    </div>
  );
}

function ConflictCard({ onBack }: { onBack: () => void }): React.JSX.Element {
  return (
    <div className="ds-cleared ds-cleared--conflict" role="status">
      <div className="ds-cleared__tick ds-cleared__tick--warn" aria-hidden="true">!</div>
      <h2 className="ds-cleared__title">Already resolved</h2>
      <p className="ds-cleared__body">
        Someone else resolved this item first, so there’s nothing left to decide here. Your queue has
        moved on — head back to pick up the next one.
      </p>
      <Button variant="primary" onClick={onBack}>Back to review queue</Button>
    </div>
  );
}
